<template>
    <div class="select-none">
        <div v-if="isFiltered" class="py-4 mb-4 shadow rounded">
            <h2 class="font-heading font-medium text-gray-900 w-3/4 text-base mb-2 leading-none">Active Filters</h2>

            <div class="flex flex-wrap mb-1 gap-x-1">
                <template v-for="(options, name, index) in filters" v-if="hasFilters(name)">
                    <a v-for="option in options" v-if="isChecked(name, option)" href="#" @click.prevent="clearOption(name, option)" class="group px-2 py-1 font-extralight text-sm bg-gray-100 rounded border mb-1 ">
                        <span class="w-full flex items-center text-gray-900">
                            <div class="w-2 cursor-pointer mr-2">
                                <span class="fal fa-times text-gray-600 opacity-50 group-hover:opacity-100 group-hover:text-red-500"></span>
                            </div>
                            <span v-if="hasComponent(name)" :is="getTagComponent(name)" :option="option" :value="models[name]"></span>
                            <span v-else v-text="option.tag || option.display"></span>
                        </span>
                    </a>
                </template>
            </div>

            <a href="#" class="flex items-center justify-center w-full bg-gray-500 hover:bg-gray-600 uppercase text-white font-bold rounded px-3 px-2 py-2" @click.prevent="clear">
                <span class="text-xs text-center">Clear Filters</span>
            </a>
        </div>

        <div class="py-4 shadow rounded">
            <div v-for="(options, name, index) in filters">
                <a href="#" @click.prevent="toggleVisibility(name)" class="px-4 flex items-center" :class="{ 'mb-2': isExpanded(name), 'mt-4': 0 !== index }">
                    <h2 class="font-heading font-medium text-gray-900 w-3/4 text-base leading-none" v-text="label(name)"></h2>
                    <span class="w-1/4 text-gray-500 text-right">
                        <i class="fal fa-fw" :class="{
                            'fa-chevron-down': isExpanded(name),
                            'fa-chevron-right': ! isExpanded(name),
                        }"></i>
                    </span>
                </a>

                <div v-if="isExpanded(name)" class="max-h-sm overflow-y-scroll">
                    <ul>
                        <li v-if="isToggle(name)" class="px-4 font-extralight text-sm">
                            <div class="flex items-center justify-start">
                                <input :checked="anyChecked(name)" @change="clearOptions(name)" type="checkbox" class="hidden" :id="`${name.toLowerCase()}-any`">

                                <label class="w-full h-6 flex items-center group mb-1 py-1 cursor-pointer" :for="`${name.toLowerCase()}-any`">
                                    <div class="block fa-stack fa-xs cursor-pointer mr-1" :for="`${name.toLowerCase()}-any`">
                                        <span v-show="anyChecked(name)" class="fas fa-circle fa-stack-2x text-white group-hover:text-orange-50"></span>
                                        <span v-show="anyChecked(name)" class="far fa-circle fa-stack-2x text-gray-300 group-hover:text-orange-200"></span>

                                        <span v-show="! anyChecked(name)" class="fas fa-circle fa-stack-2x text-orange-50"></span>
                                        <span v-show="! anyChecked(name)" class="far fa-dot-circle fa-stack-2x text-orange-400"></span>
                                    </div>

                                    <span>See all</span>
                                </label>
                            </div>
                        </li>

                        <li v-for="option in options" class="mb-1 px-4 font-extralight text-sm">
                            <div class="flex items-center justify-start">
                                <input :checked="isChecked(name, option)" @change="toggleOption(name, option)" type="checkbox" class="hidden" :id="`${name.toLowerCase()}-${option.slug}`" :value="option.slug">

                                <label class="flex gap-1 w-full items-center group mb-1 cursor-pointer" :for="`${name.toLowerCase()}-${option.slug}`">
                                    <div class="flex-0 block fa-stack fa-xs cursor-pointer" :for="`${name.toLowerCase()}-${option.slug}`">
                                        <template v-if="isToggle(name)">
                                            <span v-show="! isChecked(name, option)" class="fas fa-circle fa-stack-2x text-white group-hover:text-orange-50"></span>
                                            <span v-show="! isChecked(name, option)" class="far fa-circle fa-stack-2x text-gray-300 group-hover:text-orange-200"></span>

                                            <span v-show="isChecked(name, option)" class="fas fa-circle fa-stack-2x text-orange-50"></span>
                                            <span v-show="isChecked(name, option)" class="far fa-dot-circle fa-stack-2x text-orange-400"></span>
                                        </template>

                                        <template v-else>
                                            <span v-show="! isChecked(name, option)" class="fas fa-square fa-stack-2x text-white group-hover:text-orange-50"></span>
                                            <span v-show="! isChecked(name, option)" class="far fa-square fa-stack-2x text-gray-300 group-hover:text-orange-200"></span>

                                            <span v-show="isChecked(name, option)" class="fas fa-square fa-stack-2x text-orange-50" :class="{ 'group-hover:text-red-50': ! isDelayed(`${name.toLowerCase()}-${option.slug}`) }"></span>
                                            <span v-show="isChecked(name, option)" class="fas visible fa-check fa-stack-1x text-orange-400" :class="{ 'group-hover:invisible': ! isDelayed(`${name.toLowerCase()}-${option.slug}`) }"></span>
                                            <span v-show="isChecked(name, option)" class="fas invisible fa-times fa-stack-1x text-red-500" :class="{ 'group-hover:visible': ! isDelayed(`${name.toLowerCase()}-${option.slug}`) }"></span>
                                            <span v-show="isChecked(name, option)" class="far fa-square fa-stack-2x text-orange-400" :class="{ 'group-hover:text-red-500': ! isDelayed(`${name.toLowerCase()}-${option.slug}`) }"></span>
                                        </template>
                                    </div>

                                    <div class="flex-1">
                                        <span v-if="hasComponent(name)" :ref="getOptionComponentRef(name, option)" :is="getOptionComponent(name)" :option="option" v-model="models[name]" :active="isChecked(name, option)" @activate="activateOption(name, option)"></span>
                                        <span v-else v-text="option.display"></span>
                                    </div>
                                </label>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { omitBy } from 'lodash-es';
    import { mapKeys } from 'lodash-es';
    import { mapValues } from 'lodash-es';
    import { each } from 'lodash-es';
    import { assign } from 'lodash-es';

    import UFilterColourOption from './Filter/ColourOption.vue';
    import UFilterColourTag from './Filter/ColourTag.vue';

    import UFilterQuantityOption from './Filter/QuantityOption.vue';
    import UFilterQuantityTag from './Filter/QuantityTag.vue';

    const OPTION_TOGGLE_DELAY = 2 * 1000;

    export default {
        components: {
            UFilterColourOption, UFilterColourTag,
            UFilterQuantityOption, UFilterQuantityTag,
        },

        props: {
            filters: {
                type: Object,
                required: true,
            },

            labels: {
                type: Object,
                required: false,
                default() {
                    return {};
                },
            },

            components: {
                type: Array,
                required: false,
                default() {
                    return [];
                },
            },

            toggles: {
                type: Array,
                required: false,
                default() {
                    return [];
                },
            },

            value: {
                type: Object,
                required: false,
                default() {
                    return {};
                },
            },
        },

        data() {
            const options = Object.keys(this.filters || {});

            return {
                expanded: [],
                models: {},
                checked: {},
                delays: {},
                manuallyChecked: false,
                firstLoad: true,
            };
        },

        watch: {
            value(value) {
                this.checked = value;

                if (this.firstLoad) {
                    let expanded = [];

                    Object.keys(value).forEach(option => {
                        if (! value[option]) {
                            return;
                        }

                        expanded.push(option);
                    });

                    if (expanded.length > 0) {
                        this.expanded = expanded;
                    }
                }
            },

            checked: {
                handler() {
                    this.generateQuery();
                },

                deep: true,
            },
        },

        computed: {
            isFiltered() {
                for (let k in this.filters) {
                    if (! this.hasFilters(k)) {
                        continue;
                    }

                    return true;
                }

                return false;
            },
        },

        methods: {
            hasComponent(name) {
                return -1 !== this.components.indexOf(name);
            },

            getOptionComponentRef(name, option) {
                return `${name}Filter${option.slug}`;
            },

            getTagComponent(name) {
                return `u-filter-${name}-tag`;
            },

            getOptionComponent(name) {
                return `u-filter-${name}-option`;
            },

            isDelayed(key) {
                return !! this.delays[key];
            },

            delay(key) {
                if (this.delays[key]) {
                    clearTimeout(this.delays[key]);
                }

                this.delays[key] = setTimeout(() => {
                    delete this.delays[key];

                    this.$forceUpdate();
                }, OPTION_TOGGLE_DELAY);
            },

            label(key) {
                return this.labels[key] || key;
            },

            expand(name) {
                this.expanded.push(name);
            },

            collapse(name) {
                const index = this.expanded.indexOf(name);

                if (-1 === index) {
                    return;
                }

                this.expanded.splice(index, 1);
            },

            toggleVisibility(name) {
                if (this.isExpanded(name)) {
                    this.collapse(name);

                    return;
                }

                this.expand(name);
            },

            isExpanded(name) {
                return -1 !== this.expanded.indexOf(name);
            },

            prepareQuery() {
                let query = assign({}, this.$route.query);

                if (query.page && query.page > 1) {
                    delete query.page;
                }

                this.$router.replace({ query });
            },

            clearOptions(name) {
                this.prepareQuery();

                this.$set(this.checked, name, {});
            },

            clearOption(name, option) {
                if (! this.isChecked(name, option)) {
                    return;
                }

                this.manuallyChecked = true;

                if ('custom' === option.slug) {
                    this.$set(this.checked, name, {});

                    return;
                }

                this.$set(this.checked[name], option.slug, false);
            },

            toggleOption(name, option, force) {
                if (this.isChecked(name, option)) {
                    if (this.isToggle(name)) {
                        return;
                    }

                    this.manuallyChecked = true;

                    this.delay(`${name.toLowerCase()}-${option.slug}`);

                    this.$set(this.checked[name], option.slug, false);

                    return;
                }

                if ('custom' === option.slug && null == this.models[name]) {
                    const ref = this.getOptionComponentRef(name, option);
                    const instances = this.$refs[ref];

                    if (! Array.isArray(instances) || 0 === instances.length) {
                        return;
                    }

                    const instance = instances[0];

                    instance.$emit('focus');

                    return;
                }

                this.manuallyChecked = true;

                if (typeof this.checked[name] !== 'object' || this.isToggle(name)) {
                    this.$set(this.checked, name, {});
                }

                this.delay(`${name.toLowerCase()}-${option.slug}`);

                this.$set(this.checked[name], option.slug, true);
            },

            activateOption(name, option) {
                if (this.isChecked(name, option)) {
                    this.clearOption(name, option);
                    this.$nextTick(() => this.activateOption(name, option));

                    return;
                }

                this.toggleOption(name, option, true);
            },

            isToggle(name) {
                return -1 !== this.toggles.indexOf(name);
            },

            anyChecked(name) {
                return typeof this.checked[name] === 'object' && 0 < Object.keys(this.checked[name]).length;
            },

            isChecked(name, option) {
                if (typeof this.checked[name] !== 'object') {
                    return false;
                }

                if (this.hasComponent(name) && 'custom' === option.slug) {
                    const active = Object.keys(this.checked[name]);
                    const options = Object.values(this.filters[name]).map(option => option.slug);

                    const checked = active.filter(choice => -1 !== options.indexOf(choice));

                    return 0 === checked.length;
                }

                return !! this.checked[name][option.slug];
            },

            hasFilters(name) {
                if (undefined === this.checked[name]) {
                    return false;
                }

                const values = Object.values(this.checked[name]);

                if (0 === values.length) {
                    return false;
                }

                return 0 < values.filter(v => true === v).length;
            },

            clear() {
                this.checked = {};
            },

            generateQuery() {
                const keys = Object.keys(this.checked);
                const filters = keys.filter(k => this.hasFilters(k));

                let query = {};

                const prefix = 'filter[';
                const prefixLength = prefix.length;
                Object.keys(this.$route.query).forEach(k => {
                    if (this.manuallyChecked && 'page' === k) {
                        return;
                    }

                    if (-1 === k.indexOf(prefix) || 'filter[search]' === k) {
                        query[k] = this.$route.query[k];

                        return;
                    }
                });

                this.manuallyChecked = false;

                Object.keys(this.checked).forEach(k => {
                    if (! this.hasFilters(k)) {
                        return;
                    }

                    let options = Object.keys(this.checked[k])
                        .filter(o => this.checked[k][o]);

                    if (this.hasComponent(k) && 1 === options.length && 'custom' === options[0]) {
                        options = this.models[k];

                        if (null == options) {
                            return;
                        }

                        if (! Array.isArray(options)) {
                            options = [options];
                        }
                    }

                    query[`filter[${k.toLowerCase()}]`] = options.join(',');
                });

                this.$router.push(assign({}, this.$route, { query }));
            },
        },
    };
</script>
